import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function DimSumDelightsInChinatown() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "dim-sum-delights-chinatown-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Jade Dynasty Restaurant",
    "section-2": "Floata Seafood Restaurant",
    "section-3": "New Town Bakery & Restaurant",
    "section-4": "Kam Wai Dim Sum",
    "section-5": "Bao Bei Chinese Brasserie",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.jadedynastyvancouver.com/",
      label: "Jade Dynasty Official Website",
    },
    {
      url: "https://www.newtownbakery.ca/",
      label: "New Town Bakery - Official Site",
    },
    {
      url: "https://www.destinationvancouver.com/vancouver/neighbourhoods/chinatown/",
      label: "Destination Vancouver – Chinatown Guide",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.28070,
      lng: -123.10100,
      popupText: "Jade Dynasty Restaurant",
    },
    {
      id: "section-2",
      lat: 49.27950,
      lng: -123.10000,
      popupText: "Floata Seafood Restaurant",
    },
    {
      id: "section-3",
      lat: 49.28050,
      lng: -123.10100,
      popupText: "New Town Bakery & Restaurant",
    },
    {
      id: "section-4",
      lat: 49.28061,
      lng: -123.09844,
      popupText: "Kam Wai Dim Sum",
    },
    {
      id: "section-5",
      lat: 49.27960,
      lng: -123.10033,
      popupText: "Bao Bei Chinese Brasserie",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Dim Sum Delights: Vancouver’s Best Chinatown Finds</title>
        <meta
          name="description"
          content="Discover the top dim sum spots in Vancouver's Chinatown, from traditional tea houses to modern fusion eateries. Get tips on location, pricing, and must-try dishes."
        />
        <meta
          name="keywords"
          content="dim sum, Vancouver, Chinatown, restaurants, travel, food guide"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/dim-sum-delights-chinatown-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Dim Sum Delights: Vancouver’s Best Chinatown Finds"
        />
        <meta
          property="og:description"
          content="Explore the must-visit dim sum restaurants in Vancouver’s Chinatown, complete with coordinates, prices, and insider tips."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/dim-sum-delights-chinatown-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/dim-sum-delights-chinatown-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Dim Sum Delights: Vancouver’s Best Chinatown Finds"
        />
        <meta
          name="twitter:description"
          content="Explore the must-visit dim sum restaurants in Vancouver’s Chinatown, complete with coordinates, prices, and insider tips."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/dim-sum-delights-chinatown-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Hot dim sum dishes served in Vancouver's Chinatown"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s historic Chinatown is a must-visit for dim sum
              lovers—some even claim it rivals Hong Kong. Stepping into this
              vibrant neighborhood, you’ll find steaming dumplings, savory buns,
              and fragrant teas that reflect decades of Chinese culinary
              tradition. In this guide, we’ll explore the best dim sum spots,
              share local tips, and point out must-try dishes. Each location
              includes coordinates, so you can easily map out your very own dim
              sum adventure!
            </p>

            {/* SECTION: Jade Dynasty Restaurant */}
            <h2 id="section-1">Jade Dynasty Restaurant</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: 137 East Pender St</strong>{" "}
              <Marker
                coordY={49.28070}
                coordX={-123.10100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <em>Hours: Mon, Wed–Sun 9:00am – 8:45pm; Tue closed</em> (dim sum
              served until 3:00pm). Price Range: $$ (most dim sum ~$5–$7
              each).
            </p>
            <p>
              Jade Dynasty is the place to go if you’re after classic Cantonese
              flavors. Known for its crystal prawn dumplings (har gow) and
              tobiko-topped siu mai, this bustling tea house draws morning
              crowds seeking fresh, hot dim sum. Instead of push-carts, you fill
              out an order sheet and the dishes are brought straight from the
              kitchen. It’s a convenient setup, ensuring each bite is served
              piping hot. If you’re new to dim sum, don’t hesitate to ask the
              servers for recommendations or clarifications on the menu.
              You’ll be rewarded with some of the most authentic tastes in
              Chinatown.
            </p>

            {/* SECTION: Floata Seafood Restaurant */}
            <h2 id="section-2">Floata Seafood Restaurant</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: 400–180 Keefer St</strong>{" "}
              <Marker
                coordY={49.27950}
                coordX={-123.10000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <em>Hours: Daily Dim Sum 9:00am – 3:00pm; Dinner 5:00pm –
              9:30pm</em>. Price Range: $$ – $$$.
            </p>
            <p>
              Floata is reputedly the largest Chinese restaurant in Canada,
              boasting space for up to 1,000 diners. Offering a banquet-style
              setting perfect for big gatherings, it features a classic array of
              Cantonese dim sum favorites alongside chef’s specials and
              fresh seafood. You won’t wait long for a table here, even on
              weekends. Parking is available underground with validation, and
              reservations are encouraged for large groups. Don’t miss their har
              gow and siu mai—made with top-quality shrimp and pork—and their
              signature lotus leaf sticky rice. The atmosphere is lively,
              especially during weekend brunch when families and friends gather
              to share steaming baskets of delicious bites.
            </p>

            {/* SECTION: New Town Bakery & Restaurant */}
            <h2 id="section-3">New Town Bakery & Restaurant</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: 148 East Pender St</strong>{" "}
              <Marker
                coordY={49.28050}
                coordX={-123.10100}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <em>Hours: 6:30am – 7:30pm daily (closed statutory holidays)</em>.
              Price Range: $ (budget-friendly).
            </p>
            <p>
              A family-run bakery that’s been around since 1980, New Town
              Bakery is an institution for both sweet and savory treats.
              While famous for its bao—particularly the steamed pork buns—New
              Town also offers a concise selection of dim sum staples like siu
              mai, shrimp dumplings, and congee. Prices are very
              wallet-friendly; you can grab a satisfying meal of noodles and
              dumplings for under $15. Whether you want a quick bite on the go
              or a casual sit-down meal, New Town’s fresh, flavorful, and
              widely beloved offerings won’t disappoint.
            </p>

            {/* SECTION: Kam Wai Dim Sum */}
            <h2 id="section-4">Kam Wai Dim Sum</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: 249 East Pender St</strong>{" "}
              <Marker
                coordY={49.28061}
                coordX={-123.09844}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <em>Hours: 9:00am – 6:00pm daily</em>. Price Range: $.
            </p>
            <p>
              Kam Wai is renowned for its grab-and-go style dim sum, perfect if
              you’re on a Chinatown food crawl. It’s a small shop with limited
              seating, focusing on takeout and quick service. Their pork siu mai
              and char siu buns are popular, as is their frozen dim sum selection
              that allows you to steam authentic dumplings at home. The menu is
              simple, the price is right, and the flavors are consistently on
              point. Locals have flocked here for decades, calling it “cheap,
              hot, and ready” dim sum—an ideal stop for sampling a couple of
              treats without a large sit-down commitment.
            </p>

            {/* SECTION: Bao Bei Chinese Brasserie */}
            <h2 id="section-5">Bao Bei Chinese Brasserie</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location: 163 Keefer St</strong>{" "}
              <Marker
                coordY={49.27960}
                coordX={-123.10033}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              <em>Hours: Mon–Thu & Sun 5:30pm – 10:00pm; Fri–Sat 5:30pm –
              12:00am</em>. Price Range: $$ – $$$.
            </p>
            <p>
              Though not a traditional morning dim sum house, Bao Bei serves up a
              modern twist on Cantonese cuisine in a hip brasserie vibe. Small
              plates designed for sharing—like potstickers, sticky rice cakes,
              and the ever-changing daily specials—reflect a fusion of old-world
              Chinese techniques and contemporary flavors. Cocktails are another
              highlight. Because Bao Bei can get crowded, aim for an early dinner
              if you don’t have a reservation. If you’re looking for an elevated
              dim sum-inspired experience in the evening, Bao Bei is the spot.
            </p>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Dim sum in Vancouver’s Chinatown typically runs from morning until
              early afternoon (around 2pm), though some places extend service
              later. Sharing dishes family-style is the norm—order several items
              and split them so everyone can sample a variety. Call ahead for
              large groups, especially on weekends. Tipping around 15–20% is
              standard. Vancouver’s restaurant health inspections are
              stringent—feel confident about food safety. Also, be mindful that
              some eateries prefer cash, though most accept credit/debit. Street
              parking and pay lots are available; look for underground parking at
              Floata or near Keefer St. If you’re exploring after dark, stay on
              the main streets, which are generally well-lit and active.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What time should I arrive for dim sum?</h3>
              <p>
                Most spots open by 9am. Arrive before noon on weekends for a
                better chance at avoiding long lines. If you prefer a quieter
                experience, weekdays are typically less crowded.
              </p>

              <h3>2. Do I need reservations?</h3>
              <p>
                Most traditional dim sum restaurants are first-come, first-served,
                but you can call ahead for large groups. Floata encourages
                reservations for big parties, while smaller groups usually can
                just walk in.
              </p>

              <h3>3. Is Chinatown safe to visit?</h3>
              <p>
                Yes, Chinatown is generally safe, especially during the day when
                many visitors shop and dine. As with any urban area, be mindful of
                your surroundings and secure your belongings. Evenings are fine if
                you stick to main streets like Pender, Keefer, and Main.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s Chinatown offers an unforgettable dim sum experience.
              Whether you prefer the traditional bustle of Jade Dynasty or the
              modern twist at Bao Bei, the choices are many and the flavors are
              incredible. Come hungry, share multiple dishes, and immerse yourself
              in a culinary tradition that’s been delighting locals and tourists
              for decades. Enjoy your food journey, and don’t forget to explore
              nearby sights like the Dr. Sun Yat-Sen Classical Chinese Garden to
              round out your Chinatown adventure.
            </p>
            <p>
              <em>Disclaimer:</em> Information here is based on current operating
              hours and menu offerings, which may change. Always confirm with the
              restaurant ahead of your visit.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}